import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionCard, KpiCard, Loading, EmptyState } from "@/components/common/ui";
import { VerticalTargetsEditor } from "@/components/targets/VerticalTargetsEditor";
import { formatCount, formatPercent } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Lock, Pencil, ChevronRight, Users, Eye, Home, Percent, GitCompareArrows } from "lucide-react";

const METRICS = [
  { key: "lead", label: "Leads", icon: Users },
  { key: "sv", label: "Site Visits", icon: Eye },
  { key: "booking", label: "Bookings", icon: Home },
];
const VERT_ACCENT = {
  "ATL/BTL": "hsl(var(--chart-cat-4))",
  CX: "hsl(var(--chart-cat-6))",
  Digital: "hsl(var(--chart-cat-3))",
};

const attnClass = (a) =>
  a == null ? "text-muted-foreground"
    : a >= 1 ? "text-[hsl(var(--success))]"
      : a >= 0.6 ? "text-[hsl(var(--warning))]"
        : "text-[hsl(var(--danger))]";

const attn = (ach, tgt) => (tgt > 0 ? ach / tgt : null);

/** One "Target · Achieved" pair with an attainment micro-label. */
function TgtAch({ tgt, ach, showAch = true }) {
  const a = attn(ach, tgt);
  return (
    <div className="flex items-center justify-end gap-3">
      <span className="tabular-nums text-foreground">{formatCount(tgt)}</span>
      {showAch && (
        <span className="flex min-w-[3.5rem] flex-col items-end leading-tight">
          <span className={cn("tabular-nums font-medium", attnClass(a))}>{formatCount(ach)}</span>
          {a != null && <span className={cn("text-[10px] tabular-nums", attnClass(a))}>{formatPercent(a, 0)}</span>}
        </span>
      )}
    </div>
  );
}

/** Compare pair: Benchmark vs Marketing targets with a delta chip. */
function BenchMkt({ b, m }) {
  const d = m - b;
  return (
    <div className="flex items-center justify-end gap-3">
      <span className="tabular-nums text-muted-foreground">{formatCount(b)}</span>
      <span className="flex min-w-[3.5rem] flex-col items-end leading-tight">
        <span className="tabular-nums font-medium">{formatCount(m)}</span>
        {d !== 0 && (
          <span className={cn("text-[10px] tabular-nums", d > 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]")}>
            {d > 0 ? "+" : ""}{formatCount(d)}
          </span>
        )}
      </span>
    </div>
  );
}

export function TargetsOverviewTab({ period, category, team, periodLabel }) {
  const { canEditConfig, isFull, isManager, assignedVerticals } = useAuth();
  // Vertical leads get a strictly read-only Targets view — no ratio edits, no editors.
  const canEdit = canEditConfig && !isManager;
  const [data, setData] = useState(null);
  const [ratios, setRatios] = useState({ global: 15, projects: {} });
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(isManager ? "marketing" : "benchmark"); // benchmark | marketing | compare
  const [open, setOpen] = useState({});
  const [editor, setEditor] = useState(null); // { project, kind }
  const [globalRatio, setGlobalRatio] = useState("15");
  const [rowRatio, setRowRatio] = useState({}); // local edit buffer per project

  const load = async () => {
    setLoading(true);
    const [d, r] = await Promise.all([
      api.targetsOverview({ period, category: category || undefined, team: team || undefined }),
      api.svBookingRatio(),
    ]);
    setData(d);
    setRatios(r);
    setGlobalRatio(String(r.global ?? 15));
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, category, team]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const verts = data?.verticals || ["ATL/BTL", "CX", "Digital"];
  // Map a vertical lead's assigned (spend) verticals onto the Targets taxonomy
  // so they only see the vertical split rows relevant to them.
  const managerVerts = useMemo(() => {
    const MAP = { ATL: "ATL/BTL", BTL: "ATL/BTL", Digital: "Digital", "Digital Support": "Digital", CX: "CX" };
    const s = new Set();
    (assignedVerticals || []).forEach((v) => { const t = MAP[v]; if (t) s.add(t); });
    return s;
  }, [assignedVerticals]);
  const scopedVerts = (isManager && managerVerts.size) ? verts.filter((v) => managerVerts.has(v)) : verts;
  const showAch = mode !== "compare";

  const kpiSide = mode === "marketing" ? "marketing" : "benchmark"; // compare uses benchmark as base
  const kpi = (metric) => {
    const t = totals?.[kpiSide]?.[metric] || 0;
    const a = totals?.achieved?.[metric] || 0;
    return { t, a, pct: attn(a, t) };
  };

  const saveGlobalRatio = async () => {
    const v = parseFloat(globalRatio);
    if (!v || v <= 0) { toast.error("Ratio must be greater than 0"); return; }
    if (v === ratios.global) return;
    try {
      const r = await api.saveSvBookingRatio({ scope: "global", ratio: v });
      setRatios(r);
      toast.success(`SV → Booking ratio set to 1 : ${v}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save ratio"); }
  };

  const saveRowRatio = async (project) => {
    const raw = rowRatio[project];
    if (raw == null || raw === "") return;
    const v = parseFloat(raw);
    if (!v || v <= 0) { toast.error("Ratio must be greater than 0"); return; }
    const eff = ratios.projects?.[project] ?? ratios.global;
    if (v === eff) return;
    try {
      const r = await api.saveSvBookingRatio({ scope: "project", project, ratio: v });
      setRatios(r);
      setRowRatio((s) => ({ ...s, [project]: undefined }));
      toast.success(`${project} · SV → Booking 1 : ${v}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save ratio"); }
  };

  const effRatio = (project) => rowRatio[project] ?? String(ratios.projects?.[project] ?? ratios.global ?? 15);

  if (loading && !data) return <div className="p-4"><Loading rows={6} /></div>;

  return (
    <div className="space-y-4">
      {isManager && (
        <div className="flex items-start gap-2 rounded-lg border bg-[hsl(var(--info)/0.08)] px-3 py-2 text-xs text-muted-foreground" data-testid="targets-readonly-note">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--info))]" />
          <span>These targets are <b className="text-foreground">set by leadership</b> and shown here <b className="text-foreground">read-only</b>, scoped to your assigned vertical{(assignedVerticals || []).length === 1 ? "" : "s"}.</span>
        </div>
      )}
      {/* KPI strip — target vs achieved for the active period/side */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {METRICS.map((m) => {
          const { t, a, pct } = kpi(m.key);
          return (
            <KpiCard key={m.key} label={`${m.label} · ${periodLabel}`} accent={m.key === "booking" ? "green" : m.key === "sv" ? "violet" : "blue"} icon={m.icon}
              value={<span className="font-heading text-xl font-semibold tabular-nums">{formatCount(t)}</span>}
              sub={<span className={cn("tabular-nums", attnClass(pct))}>{formatCount(a)} achieved{pct != null ? ` · ${formatPercent(pct, 0)}` : ""}</span>}
              testId={`targets-kpi-${m.key}`} />
          );
        })}
        <KpiCard label="SV → Booking ratio" accent="amber" icon={Percent}
          value={
            <div className="flex items-center gap-1.5">
              <span className="font-heading text-xl font-semibold tabular-nums">1 :</span>
              {canEdit ? (
                <Input value={globalRatio} onChange={(e) => setGlobalRatio(e.target.value)} onBlur={saveGlobalRatio}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} type="number"
                  className="h-8 w-16 text-lg font-semibold tabular-nums" data-testid="global-ratio-input" />
              ) : <span className="font-heading text-xl font-semibold tabular-nums">{ratios.global ?? 15}</span>}
            </div>
          }
          sub="Booking target = Site Visits ÷ ratio" testId="targets-kpi-ratio" />
      </div>

      <SectionCard
        title="Targets by Project"
        description="Leads, Site Visits & Bookings per project — target vs achieved. Expand a row to see the ATL/BTL · CX · Digital split. Booking targets are derived from Site-Visit targets using the SV → Booking ratio."
        testId="targets-overview-card"
        action={
          isManager ? null : (
          <ToggleGroup type="single" value={mode} onValueChange={(v) => v && setMode(v)}
            className="rounded-lg border bg-[hsl(var(--surface-2))] p-0.5" data-testid="targets-mode-toggle">
            <ToggleGroupItem value="benchmark" className="h-7 gap-1 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="targets-mode-benchmark">
              <Lock className="h-3 w-3" /> Benchmark
            </ToggleGroupItem>
            <ToggleGroupItem value="marketing" className="h-7 gap-1 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="targets-mode-marketing">
              <Pencil className="h-3 w-3" /> Marketing
            </ToggleGroupItem>
            <ToggleGroupItem value="compare" className="h-7 gap-1 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="targets-mode-compare">
              <GitCompareArrows className="h-3 w-3" /> Compare
            </ToggleGroupItem>
          </ToggleGroup>
          )
        }>
        {rows.length === 0 ? (
          <div className="p-4"><EmptyState title="No targets" message="No target rows for this selection." /></div>
        ) : (
          <div className="table-scroll">
            <table className="w-full text-sm" data-testid="targets-overview-table">
              <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th rowSpan={2} className="th-sticky-left px-3 py-2 text-left font-medium">Project</th>
                  {METRICS.map((m) => (
                    <th key={m.key} colSpan={2} className="border-l px-3 py-1.5 text-center font-medium">{m.label}</th>
                  ))}
                  {mode !== "compare" && <th rowSpan={2} className="border-l px-3 py-2 text-center font-medium">SV:Bkg</th>}
                  <th rowSpan={2} className="px-2 py-2" />
                </tr>
                <tr>
                  {METRICS.map((m) => (
                    <React.Fragment key={m.key}>
                      <th className="border-l px-3 py-1.5 text-right font-medium text-[10px]">{mode === "compare" ? "Bench" : "Target"}</th>
                      <th className="px-3 py-1.5 text-right font-medium text-[10px]">{mode === "compare" ? "Mktg" : "Achieved"}</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => {
                  const isOpen = (isManager && scopedVerts.length > 0) ? true : !!open[r.project];
                  return (
                    <React.Fragment key={r.project}>
                      <tr className="hover:bg-[hsl(var(--surface-2))]" data-testid={`targets-row-${r.project}`}>
                        <td className="td-sticky-left px-3 py-2.5">
                          <button type="button" onClick={() => setOpen((o) => ({ ...o, [r.project]: !o[r.project] }))}
                            className="inline-flex items-center gap-1.5 text-left font-medium hover:text-primary" data-testid={`targets-expand-${r.project}`}>
                            <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform duration-150", isOpen && "rotate-90")} />
                            {r.project}
                          </button>
                          {r.category && <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">{r.category}</span>}
                        </td>
                        {METRICS.map((m) => (
                          <td key={m.key} colSpan={2} className="border-l px-3 py-2.5">
                            {mode === "compare"
                              ? <BenchMkt b={r.benchmark.total[m.key]} m={r.marketing.total[m.key]} />
                              : <TgtAch tgt={r[mode].total[m.key]} ach={r.achieved.total[m.key]} showAch={showAch} />}
                          </td>
                        ))}
                        {mode !== "compare" && (
                          <td className="border-l px-2 py-2.5 text-center">
                            {canEdit ? (
                              <div className="flex items-center justify-center gap-0.5 text-xs text-muted-foreground">
                                1:
                                <Input value={effRatio(r.project)} type="number"
                                  onChange={(e) => setRowRatio((s) => ({ ...s, [r.project]: e.target.value }))}
                                  onBlur={() => saveRowRatio(r.project)}
                                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                                  className="h-7 w-14 text-right text-xs tabular-nums" data-testid={`row-ratio-${r.project}`} />
                              </div>
                            ) : <span className="text-xs tabular-nums text-muted-foreground">1:{r.ratio}</span>}
                          </td>
                        )}
                        <td className="px-2 py-2.5 text-right">
                          {mode !== "compare" && canEdit && (
                            <Button variant="ghost" size="sm" className="h-7 px-2"
                              onClick={() => setEditor({ project: r.project, kind: mode === "marketing" ? "marketing" : "benchmark" })}
                              data-testid={`targets-edit-${r.project}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                      {isOpen && scopedVerts.map((v) => (
                        <tr key={`${r.project}-${v}`} className="bg-[hsl(var(--surface-2))] text-xs" data-testid={`targets-vrow-${r.project}-${v.replace(/[^a-zA-Z]+/g, "-")}`}>
                          <td className="td-sticky-left px-3 py-1.5 pl-8">
                            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                              <span className="h-2 w-2 rounded-full" style={{ background: VERT_ACCENT[v] || "hsl(var(--primary))" }} />
                              {v}
                            </span>
                          </td>
                          {METRICS.map((m) => (
                            <td key={m.key} colSpan={2} className="border-l px-3 py-1.5">
                              {mode === "compare"
                                ? <BenchMkt b={r.benchmark.verticals[v][m.key]} m={r.marketing.verticals[v][m.key]} />
                                : <TgtAch tgt={r[mode].verticals[v][m.key]} ach={r.achieved.verticals[v][m.key]} showAch={showAch} />}
                            </td>
                          ))}
                          {mode !== "compare" && <td className="border-l" />}
                          <td />
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 bg-[hsl(var(--surface-2))] font-semibold" data-testid="targets-overview-totals">
                  <td className="td-sticky-left px-3 py-2.5">Total</td>
                  {METRICS.map((m) => (
                    <td key={m.key} colSpan={2} className="border-l px-3 py-2.5">
                      {mode === "compare"
                        ? <BenchMkt b={totals.benchmark[m.key]} m={totals.marketing[m.key]} />
                        : <TgtAch tgt={totals[mode][m.key]} ach={totals.achieved[m.key]} showAch={showAch} />}
                    </td>
                  ))}
                  {mode !== "compare" && <td className="border-l" />}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </SectionCard>

      <VerticalTargetsEditor
        open={!!editor}
        project={editor?.project}
        kind={editor?.kind || "marketing"}
        ratio={Number(ratios.projects?.[editor?.project] ?? ratios.global ?? 15)}
        canEdit={editor?.kind === "benchmark" ? isFull : canEdit}
        onClose={() => setEditor(null)}
        onSaved={load}
      />
    </div>
  );
}
